/**
 * @file ContractPricingModal.js
 * @version 2.27.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-11-10
 * @modified 2026-10-29
 * @description Modal for adding/editing contract/ spot pricing entries
 *
 * 2.27.0 (2026-10-29): contracts on a product group (Cam, 28 Oct 2026 —
 * "Plain product only, same pack"). The several-product layout (one row per
 * product, sharing the contract's dates, quantity, terms and scope) now serves
 * minerals, bags and other as well as blends and compounds — the server's
 * model already held several products for them (Contract these prices makes
 * such contracts) and the window could only show one. "Add a product group"
 * (ContractProductGroupPicker) adds a group's plain products of the
 * contract's pack as rows, each with its own item cost and supplier, the
 * price typed once for all; the contract remembers the group ("Group: <name>"
 * above its products, with "Add n new products from the group" when the group
 * has gained some since). Spot and milk powder stay one product.
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import './ContactModal.css';
import SearchableDropdown from './SearchableDropdown';
import Dropdown from './Dropdown';
import ApplyScopeControl from './ApplyScopeControl';
import OurContractNumberField from './OurContractNumberField';
import Input from './Input';
import DateInput from './DateInput';
import ConfirmDialog from './ConfirmDialog';
import AuditHistoryModal from './AuditHistoryModal';
import LinkedPurchaseOrdersPanel from './LinkedPurchaseOrdersPanel';
import ContractOrdersPanel from './ContractOrdersPanel';
import LoadingSpinner from './LoadingSpinner';
import ContractProductGroupPicker from './ContractProductGroupPicker';
import { ContractGroupBadge } from './PricingBadges';
import { GROUP_CONTRACT_CATEGORIES, newMembersOf, addNewText } from '../utils/contractProductGroups';
import { useSlideOutButton } from './SlideOutButton';
import { autoSelectSupplierFromItemCode, getProductCost, computeSuggestedPrice, computeCostWithAdditive, isCurrentPeriod, inventoryCostFor, costDiffersFromInventory } from '../utils/pricingHelpers';
import { CONTRACT_CATEGORY_CODES, codeInList, isOtherCode } from '../utils/accountCodes';
import { formatPrice } from '../utils/currencyFormatter';
import { formatDecimalInput } from '../utils/numberFormatters';
import { defaultContractDeliveryTerms } from '../utils/deliveryTerms';
import {
  isHoldingProduct, holdingProductOption, productValueFor, productPayloadFor, HoldingProductDescription
} from './HoldingProduct';

// Livestock (formerly "Herd") type options. The 'dairy youngstock' value is
// retained (35+ existing records use it) but now labelled "Youngstock".
const LIVESTOCK_TYPE_OPTIONS = [
  { value: '', label: 'Select…' },
  { value: 'dairy', label: 'Dairy' },
  { value: 'dairy youngstock', label: 'Youngstock' },
  { value: 'beef', label: 'Beef' },
  { value: 'ewe', label: 'Ewe' },
  { value: 'lamb', label: 'Lamb' }
];

function ContractPricingModal({ contract, categoryId, onClose, onSave, mode: initialMode = 'edit' }) {
  // Cost still AUTO-FILLS from the inventory item, but it is no longer locked.
  // It was read-only for blends (2), compounds (3), spot (5), bags (7) and
  // other (8), which meant a contract agreed at a cost the item had since moved
  // away from could not be recorded at all — the only way to enter the real
  // figure was to change the item, which rewrites it for everything else.
  //
  // The safeguard that replaces the lock is a note on the label when the entered
  // cost disagrees with the item's, and only while the contract sits in the
  // period we are in now. See noteFor() below.

  // Local state for mode so the footer Edit button can flip view → edit
  // without re-mounting the modal (or pushing state up to the parent).
  const [mode, setMode] = useState(initialMode);
  // One row per product (2.27.0): every category but Spot (5) and milk powder (6).
  const multiProduct = GROUP_CONTRACT_CATEGORIES.includes(categoryId);
  // The product group the contract was made from (2.27.0), the groups this
  // window can add (GET /api/pricing/contract-product-groups), the products
  // taken from a group in this session (said in the history) and a request
  // to open the picker on the recorded group.
  const [productGroupId, setProductGroupId] = useState(contract?.product_group?.id ?? null);
  const [productGroupName, setProductGroupName] = useState(contract?.product_group?.name ?? '');
  const [productGroupArchived, setProductGroupArchived] = useState(!!contract?.product_group?.is_archived);
  const [groupInfo, setGroupInfo] = useState(null);
  const [groupRequest, setGroupRequest] = useState(null);
  const [addedFromGroup, setAddedFromGroup] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [additives, setAdditives] = useState([]);
  const [reps, setReps] = useState([]);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState('');
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  // True while the initial reference-data fetch is in flight. Drives the
  // body-overlay LoadingSpinner so the user sees progress instead of an
  // empty form while customers / products / suppliers / additives / reps
  // (and, for group contracts, the group's other members) load.
  const [isInitializing, setIsInitializing] = useState(true);

  const [showHistory, setShowHistory] = useState(false);
  const [suggestedPrice, setSuggestedPrice] = useState(null);
  const [contractHistory, setContractHistory] = useState([]);
  const [showContractHistory, setShowContractHistory] = useState(false);

  // For compound group editing: multiple product lines. The supplier
  // contract number lives at the LINE level so each product within a
  // group can carry its own supplier reference. Quantity used to live
  // here but is now shared at the contract level (see formData.quantity).
  const emptyLine = { inventory_item_id: '', holding_product_name: '', additive_id: '', supplier_contact_id: '', mill: '', cost: '', price: '', margin: '', supplier_contract_number: '', is_fulfilled_override: false };
  const [groupLines, setGroupLines] = useState([{ ...emptyLine }]);

  // Pricing-group scope: which accounts this price applies to. Only relevant when the
  // selected customer is in a pricing group (linked accounts with the Pricing scope on).
  // 'parent' = just the owner account (default, = legacy behaviour); 'group' = whole live
  // group; 'specific' = the explicit subset in scopeAccountIds.
  const [pricingGroup, setPricingGroup] = useState([]); // [{ id, account_no, company_name, is_parent }]
  const [applyScope, setApplyScope] = useState('parent');
  const [scopeAccountIds, setScopeAccountIds] = useState([]);

  const [formData, setFormData] = useState({
    account_id: '',
    inventory_item_id: '',
    // The description of a holding product (inventory_item_id = HOLDING_PRODUCT).
    holding_product_name: '',
    additive_id: '',
    supplier_contact_id: '',
    start_date: '',
    end_date: '',
    quantity: '',
    cost: '',
    price: '',
    margin: '',
    contract_number: '',
    supplier_contract_number: '', // only used on the non-group (Spot) path
    rep_name: '',
    herd_type: '',
    // A new contract starts on its category's usual terms: Direct Delivery for
    // straights and minerals, Delivery for the rest. A spot or "Other" price
    // can be for anything, so the product chosen settles it (handleChange).
    delivery_terms: contract ? '' : defaultContractDeliveryTerms({ categoryId }),
    notes: '',
    is_draft: false,
    // Force-fulfilled override for non-group flows (Mineral / Spot). Groups
    // carry their own per-line override on each groupLine.
    is_fulfilled_override: false
  });
  // Slideout buttons — re-measure when mode flips (view↔edit) so the new
  // Edit / Cancel labels size correctly, and when the draft toggle changes
  // "Update" ↔ "Update draft".
  useSlideOutButton([mode, formData.is_draft]);
  // Set once the user picks delivery terms themselves, so choosing a product
  // afterwards doesn't overwrite them.
  const deliveryTermsChosenRef = useRef(false);

  // Our contract number is between Excel Agri and the customer — a single
  // value lives on `formData.contract_number`. Supplier contract numbers
  // live per-line for groups (each product's mill/supplier reference) and
  // on formData.supplier_contract_number for the non-group single path.

  useEffect(() => {
    // Promise.all so we know exactly when the reference-data load is
    // complete — drives the body LoadingSpinner overlay. Console-log the
    // total ms so we can see where the time goes if it ever drags.
    (async () => {
      const t0 = performance.now();
      try {
        await Promise.all([
          loadCustomers(),
          loadSuppliers(),
          loadProducts(),
          loadAdditives(),
          loadReps(),
          loadProductGroups()
        ]);
        console.log(`[ContractPricingModal] reference-data load: ${Math.round(performance.now() - t0)}ms`);
      } finally {
        setIsInitializing(false);
      }
    })();

    // Prevent background scrolling
    document.body.classList.add('modal-open');
    const contentWrapper = document.querySelector('.content-wrapper');
    if (contentWrapper) {
      contentWrapper.classList.add('modal-open');
    }

    // Format date string safely (avoids timezone shift from Date objects)
    const formatDateStr = (d) => {
      if (!d) return '';
      if (typeof d === 'string' && d.match(/^\d{4}-\d{2}-\d{2}$/)) return d;
      // If ISO string or Date object, extract date in local timezone
      const date = new Date(d);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Pre-populate form if editing
    if (contract) {
      // Set shared fields from the contract
      setFormData({
        account_id: contract.account_id != null ? contract.account_id.toString() : '',
        inventory_item_id: productValueFor(contract),
        holding_product_name: contract.holding_product_name ?? '',
        additive_id: contract.additive_id != null ? contract.additive_id.toString() : '',
        supplier_contact_id: contract.supplier_contact_id != null ? contract.supplier_contact_id.toString() : '',
        start_date: formatDateStr(contract.start_date),
        end_date: formatDateStr(contract.end_date),
        // Loaded at 2dp minimum, up to 4 where the figure has them — "236.125",
        // not the column's "236.1250". Same number: the margin maths parses it
        // and the server stores it into a numeric column.
        quantity: contract.quantity != null ? formatDecimalInput(contract.quantity) : '',
        cost: contract.cost != null ? formatDecimalInput(contract.cost) : '',
        price: contract.price != null ? formatDecimalInput(contract.price) : '',
        margin: contract.margin != null ? contract.margin : '',
        contract_number: contract.contract_number ?? '',
        supplier_contract_number: contract.supplier_contract_number ?? '',
        rep_name: contract.rep_name ?? '',
        herd_type: contract.herd_type ?? '',
        delivery_terms: contract.delivery_terms ?? '',
        notes: contract.notes ?? '',
        is_draft: !!contract.is_draft,
        is_fulfilled_override: !!contract.is_fulfilled_override
      });
      // Prefill the pricing-group apply scope from the existing contract.
      setApplyScope(['parent', 'group', 'specific'].includes(contract.apply_scope) ? contract.apply_scope : 'parent');
      setScopeAccountIds(Array.isArray(contract.scope_accounts) ? contract.scope_accounts.map(a => a.id) : []);

      // Our contract number (Excel Agri ↔ customer) is a single string
      // stored as-is — no more parsing into badges. Supplier contract
      // number is now per-product-line for groups; for non-group (Spot)
      // it lives on formData.supplier_contract_number, set above.

      // Blend (2) and Compound (3) support multi-product contract groups.
      if (multiProduct && contract.contract_group_id) {
        loadGroupLines(contract.contract_group_id);
      } else if (multiProduct) {
        // Single contract — show as one line in the group editor.
        // Per-line supplier_contract_number; quantity is now shared at
        // formData level, not per-line.
        setGroupLines([{
          id: contract.id,
          inventory_item_id: productValueFor(contract),
          holding_product_name: contract.holding_product_name ?? '',
          additive_id: contract.additive_id != null ? contract.additive_id.toString() : '',
          supplier_contact_id: contract.supplier_contact_id != null ? contract.supplier_contact_id.toString() : '',
          mill: contract.mill ?? '',
          cost: contract.cost != null ? formatDecimalInput(contract.cost) : '',
          price: contract.price != null ? formatDecimalInput(contract.price) : '',
          margin: contract.margin != null ? contract.margin : '',
          supplier_contract_number: contract.supplier_contract_number ?? '',
          is_fulfilled_override: !!contract.is_fulfilled_override
        }]);
      }
    } else {
      // Set default dates based on category
      if (categoryId === 5) {
        // Spot pricing: default to start to end of next month
        const today = new Date();
        const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
        const endOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 2, 0);
        const pad = (n) => n.toString().padStart(2, '0');
        const startDate = `${nextMonth.getFullYear()}-${pad(nextMonth.getMonth() + 1)}-${pad(nextMonth.getDate())}`;
        const endDate = `${endOfNextMonth.getFullYear()}-${pad(endOfNextMonth.getMonth() + 1)}-${pad(endOfNextMonth.getDate())}`;
        setFormData(prev => ({ ...prev, start_date: startDate, end_date: endDate }));
      } else {
        // Contract pricing: default to current period
        const { startDate, endDate } = getCurrentPeriodDates();
        setFormData(prev => ({ ...prev, start_date: startDate, end_date: endDate }));
      }
    }

    return () => {
      document.body.classList.remove('modal-open');
      const contentWrapper = document.querySelector('.content-wrapper');
      if (contentWrapper) {
        contentWrapper.classList.remove('modal-open');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract]);

  // Load the selected customer's pricing group (parent + pricing-linked children).
  // Empty when the account is in no pricing group → the "Applies to" control hides
  // and single-account customers see no change.
  useEffect(() => {
    const accountId = formData.account_id;
    if (!accountId) { setPricingGroup([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/contacts/pricing-group/${accountId}`, { credentials: 'include' });
        if (!res.ok) { if (!cancelled) setPricingGroup([]); return; }
        const data = await res.json();
        if (cancelled) return;
        const members = data.members || [];
        setPricingGroup(members);
        // If the account isn't in a group, force scope back to 'parent'.
        if (members.length <= 1) { setApplyScope('parent'); setScopeAccountIds([]); }
      } catch (e) {
        if (!cancelled) setPricingGroup([]);
      }
    })();
    return () => { cancelled = true; };
  }, [formData.account_id]);

  const loadCustomers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contacts/search?limit=10000&contactStatus=all&accountType=CUSTOMER`, {
        credentials: 'include'
      });
      const data = await response.json();
      setCustomers(data.contacts || []);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadSuppliers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contacts/search?limit=10000&contactStatus=all&accountType=SUPPLIER`, {
        credentials: 'include'
      });
      const data = await response.json();
      setSuppliers(data.contacts || []);
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pricing/inventory-items`, {
        credentials: 'include'
      });
      const items = await response.json();

      // Filter by sales account code based on category. Each contract-pricing
      // tab lists products from its own sales-account-code bucket (see
      // CONTRACT_CATEGORY_CODES). "Other" (8) is the catch-all: any code not
      // claimed by another pricing area. Codes match case-insensitively.
      //   1 Straights (204) · 2 Blends (202) · 3 Compounds (201)
      //   4 Minerals (206)  · 6 Milk Powder (200P)
      //   7 Bags (207/207A/200N) · 8 Other (everything else)
      const codeBuckets = {
        1: CONTRACT_CATEGORY_CODES.straights,
        2: CONTRACT_CATEGORY_CODES.blends,
        3: CONTRACT_CATEGORY_CODES.compounds,
        4: CONTRACT_CATEGORY_CODES.minerals,
        6: CONTRACT_CATEGORY_CODES.milkPowder,
        7: CONTRACT_CATEGORY_CODES.bags,
      };

      let filteredItems;
      if (categoryId === 8) {
        filteredItems = items.filter(item => isOtherCode(item.sales_account_code));
      } else if (codeBuckets[categoryId]) {
        filteredItems = items.filter(item => codeInList(item.sales_account_code, codeBuckets[categoryId]));
      } else {
        filteredItems = items;
      }

      // Milk Powder cost comes from the weekly cost-price table, not the
      // inventory item's purchase_unit_price. Overlay each product's `.cost`
      // with its latest weekly cost so getProductCost() / suggested-price all
      // work unchanged in the single-product flow below.
      if (categoryId === 6 && filteredItems.length > 0) {
        try {
          const mpRes = await fetch(`${API_URL}/api/pricing/milk-powder/cost-prices`, { credentials: 'include' });
          const mpData = await mpRes.json();
          const costById = new Map((mpData.data || []).map(r => [r.inventory_item_id, r.current_cost]));
          filteredItems.forEach(item => {
            if (costById.has(item.id)) item.cost = costById.get(item.id);
          });
        } catch (e) {
          console.error('Error loading milk powder weekly costs:', e);
        }
      }

      setProducts(filteredItems || []);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const loadAdditives = async () => {
    try {
      const response = await fetch(`${API_URL}/api/additives`, {
        credentials: 'include'
      });
      const result = await response.json();
      setAdditives(result.data || []);
    } catch (error) {
      console.error('Error loading additives:', error);
    }
  };

  /**
   * The inventory item's cost, when it disagrees with what has been typed and
   * the disagreement is worth raising.
   *
   * Only while the contract sits in the CURRENT period. On a past or future one
   * the contract was priced against its own period's cost, and today's item cost
   * says nothing about whether it is right — flagging those is noise that
   * teaches people to ignore the notice that matters.
   *
   * Returns the inventory figure, or null for "nothing to say".
   */
  const costNoteFor = (itemId, additiveId, enteredCost) => {
    if (!isCurrentPeriod(formData.start_date, formData.end_date)) return null;
    if (!itemId) return null;
    const product = products.find(p => p.id.toString() === String(itemId));
    const additive = additiveId ? additives.find(a => a.id.toString() === String(additiveId)) : null;
    return costDiffersFromInventory(enteredCost, inventoryCostFor(product, additive));
  };

  // Calculate current period dates based on today's date
  const getCurrentPeriodDates = () => {
    const today = new Date();
    const currentMonth = today.getMonth() + 1; // 1-12
    const currentYear = today.getFullYear();

    // Winter period: October 1 - April 30 (next year)
    // Summer period: May 1 - September 30

    // Check if we're in Winter period (Oct 1 to Apr 30)
    const isWinter = (currentMonth >= 10) || (currentMonth <= 4);

    if (isWinter) {
      // Winter period
      let winterStartYear = currentYear;
      let winterEndYear = currentYear + 1;

      // If we're in Jan-Apr, the winter period started last year
      if (currentMonth <= 4) {
        winterStartYear = currentYear - 1;
        winterEndYear = currentYear;
      }

      const startDate = `${winterStartYear}-10-01`;
      const endDate = `${winterEndYear}-04-30`;
      return { startDate, endDate };
    } else {
      // Summer period (May 1 - September 30)
      const startDate = `${currentYear}-05-01`;
      const endDate = `${currentYear}-09-30`;
      return { startDate, endDate };
    }
  };

  // Calculate period dates N steps from current (0 = current, 1 = next, 2 = next+1)
  const getPeriodDatesOffset = (offset) => {
    let { startDate } = getCurrentPeriodDates();
    let start = new Date(startDate);

    for (let i = 0; i < offset; i++) {
      const month = start.getMonth() + 1;
      if (month === 10) {
        // Winter → next is summer
        start = new Date(start.getFullYear() + 1, 4, 1);
      } else {
        // Summer → next is winter
        start = new Date(start.getFullYear(), 9, 1);
      }
    }

    const month = start.getMonth() + 1;
    if (month >= 5 && month <= 9) {
      return { startDate: `${start.getFullYear()}-05-01`, endDate: `${start.getFullYear()}-09-30` };
    }
    const wy = start.getFullYear();
    return { startDate: `${wy}-10-01`, endDate: `${wy + 1}-04-30` };
  };

  // Calculate period name from start date (matches server-side getCanonicalPeriod logic)
  const calculatePeriodName = (startDate) => {
    if (!startDate) return '';

    const d = new Date(startDate);
    const month = d.getMonth() + 1; // 1-12
    const year = d.getFullYear();

    if (month >= 5 && month <= 9) {
      return `Summer ${year}`;
    }

    // Winter: Oct-Dec → starts this year, Jan-Apr → started previous year
    const winterStartYear = month >= 10 ? year : year - 1;
    const winterEndYear = winterStartYear + 1;
    const endYearShort = winterEndYear.toString().slice(-2);
    return `Winter ${winterStartYear}-${endYearShort}`;
  };


  const loadReps = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contacts/sales-reps`, {
        credentials: 'include'
      });
      const data = await response.json();
      setReps((data.reps || []).map(r => r.name));
    } catch (error) {
      console.error('Error loading reps:', error);
    }
  };

  // The product groups this window can add (2.27.0). Read-only; a refusal
  // (a rep) or a failure leaves the step greyed with the reason.
  const loadProductGroups = async () => {
    if (!GROUP_CONTRACT_CATEGORIES.includes(categoryId)) return;
    try {
      const res = await fetch(`${API_URL}/api/pricing/contract-product-groups?category_id=${categoryId}`, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setGroupInfo({ ready: false, groups_ready: false, reason: data.error || 'Product groups could not be loaded', total: 0, groups: [] });
        return;
      }
      setGroupInfo(data);
    } catch (e) {
      setGroupInfo({ ready: false, groups_ready: false, reason: 'Product groups could not be loaded — check the connection', total: 0, groups: [] });
    }
  };

  const loadGroupLines = async (groupId) => {
    try {
      // Use the contract_group_id server-side filter so the view only
      // computes remaining_quantity / linked_po_count for the group's
      // ~2-3 members instead of every Blend / Compound contract in the
      // category. Before this, opening a group modal triggered the same
      // expensive view computation as a full page load.
      const response = await fetch(`${API_URL}/api/pricing/contracts?contract_group_id=${encodeURIComponent(groupId)}`, {
        credentials: 'include'
      });
      const data = await response.json();
      const groupContracts = data.data || [];
      const recorded = groupContracts.map(c => c.product_group).find(Boolean);
      if (recorded) {
        setProductGroupId(recorded.id);
        setProductGroupName(recorded.name);
        setProductGroupArchived(!!recorded.is_archived);
      }
      if (groupContracts.length > 0) {
        setGroupLines(groupContracts.map(c => ({
          id: c.id,
          inventory_item_id: productValueFor(c),
          holding_product_name: c.holding_product_name ?? '',
          additive_id: c.additive_id != null ? c.additive_id.toString() : '',
          supplier_contact_id: c.supplier_contact_id != null ? c.supplier_contact_id.toString() : '',
          mill: c.mill ?? '',
          cost: c.cost != null ? formatDecimalInput(c.cost) : '',
          price: c.price != null ? formatDecimalInput(c.price) : '',
          margin: c.margin != null ? c.margin : '',
          supplier_contract_number: c.supplier_contract_number ?? '',
          is_fulfilled_override: !!c.is_fulfilled_override
        })));
      }
    } catch (error) {
      console.error('Error loading group lines:', error);
    }
  };

  const handleGroupLineChange = (index, field, value) => {
    setGroupLines(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // Auto-fill cost + supplier when the product changes on a group line.
      if (field === 'inventory_item_id' && value) {
        const selectedProduct = products.find(p => p.id.toString() === value);
        if (selectedProduct) {
          const supplier = autoSelectSupplierFromItemCode(selectedProduct.item_code, suppliers);
          if (supplier) updated[index].supplier_contact_id = supplier.id.toString();
          updated[index].additive_id = '';
          updated[index].price = '';
          updated[index].margin = '';
          const cost = getProductCost(selectedProduct);
          if (cost !== null) updated[index].cost = cost;
        }
      }

      // Re-compute cost when additive changes — adds (or removes) the
      // additive price on top of the base product cost. No margin on the
      // additive itself; it just passes through.
      if (field === 'additive_id' && updated[index].inventory_item_id) {
        const selectedProduct = products.find(p => p.id.toString() === updated[index].inventory_item_id);
        const base = getProductCost(selectedProduct);
        if (base !== null) {
          let additivePrice = 0;
          if (value) {
            const a = additives.find(a => a.id.toString() === value);
            if (a && a.price) additivePrice = parseFloat(a.price) || 0;
          }
          updated[index].cost = computeCostWithAdditive(base, additivePrice);
          if (updated[index].price) {
            updated[index].margin = (parseFloat(updated[index].price) || 0) - updated[index].cost;
          } else {
            updated[index].margin = '';
          }
        }
      }

      // Auto-calculate margin on cost / price edits
      if (field === 'cost' || field === 'price') {
        const cost = parseFloat(field === 'cost' ? value : updated[index].cost) || 0;
        const price = parseFloat(field === 'price' ? value : updated[index].price) || 0;
        updated[index].margin = price - cost;
      }
      return updated;
    });
    // A new price follows its first product's category until terms are picked
    // by hand (the one-product layout did this; "Other" can be anything).
    if (field === 'inventory_item_id' && value && index === 0 && !contract && !deliveryTermsChosenRef.current) {
      const selectedProduct = products.find(p => p.id.toString() === value);
      if (selectedProduct) {
        setFormData(prev => ({
          ...prev,
          delivery_terms: defaultContractDeliveryTerms({ categoryId, salesAccountCode: selectedProduct.sales_account_code })
        }));
      }
    }
  };

  // "Add a product group" (2.27.0): the group's plain products of the
  // contract's pack, each a product row with its own item cost and supplier,
  // the price typed once for all. An untouched empty row is replaced.
  const addFromGroup = ({ group, members, price }) => {
    if (error) setError('');
    setGroupLines(prev => {
      const keep = prev.filter(l => l.id || l.inventory_item_id);
      const added = members.map(m => {
        const product = products.find(p => String(p.id) === String(m.id));
        const base = getProductCost(product);
        const cost = base !== null ? base : (m.cost !== null && m.cost !== undefined ? Number(m.cost) : null);
        const known = m.supplier && m.supplier.id && suppliers.some(s => String(s.id) === String(m.supplier.id));
        const byCode = known ? null : autoSelectSupplierFromItemCode(m.item_code, suppliers);
        const supplierId = known ? String(m.supplier.id) : (byCode ? byCode.id.toString() : '');
        const priced = price !== '' && price !== null && price !== undefined;
        return {
          ...emptyLine,
          inventory_item_id: String(m.id),
          supplier_contact_id: supplierId,
          cost: cost === null ? '' : cost,
          price: priced ? price : '',
          margin: priced && cost !== null ? (parseFloat(price) || 0) - cost : ''
        };
      });
      return [...keep, ...added];
    });
    setProductGroupId(group.id);
    setProductGroupName(group.name);
    setProductGroupArchived(!!group.is_archived);
    setAddedFromGroup(prev => [...new Set([...prev, ...members.map(m => Number(m.id))])]);
  };

  // Overwrite the supplier contract number on every group line with `value`
  // (including a blank), so one shared contract number can be applied in a click.
  const applyContractNumberToAll = (value) => {
    setGroupLines(prev => prev.map(line => ({ ...line, supplier_contract_number: value })));
  };

  const addGroupLine = () => {
    setGroupLines(prev => [...prev, { ...emptyLine }]);
  };

  const removeGroupLine = (index) => {
    setGroupLines(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : prev);
  };

  const loadContractHistory = async (accountId) => {
    try {
      const response = await fetch(`${API_URL}/api/pricing/contracts/history/${accountId}`, {
        credentials: 'include'
      });
      const data = await response.json();
      setContractHistory(data);
    } catch (error) {
      console.error('Error loading contract history:', error);
    }
  };

  // Load history on mount if editing existing contract
  useEffect(() => {
    if (contract && contract.account_id) {
      loadContractHistory(contract.account_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract]);

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      onClose();
    }, 200);
  };

  const handleChange = (field, value) => {
    // Clear error when user makes changes
    if (error) setError('');
    if (field === 'delivery_terms') deliveryTermsChosenRef.current = true;

    setFormData(prev => {
      const updated = { ...prev, [field]: value };

      // Auto-calculate margin when cost or price changes
      if (field === 'cost' || field === 'price') {
        const cost = parseFloat(field === 'cost' ? value : updated.cost) || 0;
        const price = parseFloat(field === 'price' ? value : updated.price) || 0;
        updated.margin = price - cost;
      }

      // Auto-select supplier based on product code prefix and auto-fill cost.
      // Cost = product cost + additive price (additive baked in, since the
      // additive doesn't get its own margin — it just passes through).
      if (field === 'inventory_item_id' && value) {
        const selectedProduct = products.find(p => p.id.toString() === value);
        if (selectedProduct) {
          const supplier = autoSelectSupplierFromItemCode(selectedProduct.item_code, suppliers);
          if (supplier) updated.supplier_contact_id = supplier.id.toString();
          // A new price follows the product's category until terms are picked by hand.
          if (!contract && !deliveryTermsChosenRef.current) {
            updated.delivery_terms = defaultContractDeliveryTerms({
              categoryId, salesAccountCode: selectedProduct.sales_account_code
            });
          }
          updated.additive_id = '';
          updated.price = '';
          updated.margin = '';
          setSuggestedPrice(null);

          const cost = getProductCost(selectedProduct);
          if (cost !== null) {
            updated.cost = cost;
            updated.margin = '';
          }
        }
      }

      // Re-compute cost when additive changes — adds (or removes) the
      // additive price on top of the base product cost.
      if (field === 'additive_id' && updated.inventory_item_id) {
        const selectedProduct = products.find(p => p.id.toString() === updated.inventory_item_id);
        const base = getProductCost(selectedProduct);
        if (base !== null) {
          let additivePrice = 0;
          if (value) {
            const a = additives.find(a => a.id.toString() === value);
            if (a && a.price) additivePrice = parseFloat(a.price) || 0;
          }
          updated.cost = computeCostWithAdditive(base, additivePrice);
          // Recalc margin from current price
          if (updated.price) {
            updated.margin = (parseFloat(updated.price) || 0) - updated.cost;
          } else {
            updated.margin = '';
          }
        }
      }

      // Auto-populate rep and load contract history when customer is selected
      if (field === 'account_id' && value) {
        const selectedCustomer = customers.find(c => c.id.toString() === value);
        if (selectedCustomer && selectedCustomer.sales_rep) {
          updated.rep_name = selectedCustomer.sales_rep;
        }
        // Load contract history for this customer
        loadContractHistory(value);
      }

      // Suggested price — formula + margin live in pricingHelpers. Cost
      // already includes the additive price (set on additive_id change),
      // so the helper just adds the margin on top.
      if (field === 'cost' || field === 'additive_id' || field === 'inventory_item_id' || field === 'price') {
        if (updated.price) {
          setSuggestedPrice(null);
        } else {
          setSuggestedPrice(computeSuggestedPrice(categoryId, { cost: updated.cost }));
        }
      }

      return updated;
    });
  };

  const validateForm = () => {
    // Clear previous error
    setError('');

    // Check required fields
    if (!formData.account_id) {
      setError('Please select a customer');
      return false;
    }

    // Several-product contracts (2.27.0: every category but Spot and milk
    // powder) store products per line; the others use formData.inventory_item_id.
    if (multiProduct) {
      if (!groupLines || groupLines.length === 0 || groupLines.some(l => !l.inventory_item_id)) {
        setError('Please select a product for each line');
        return false;
      }
      if (groupLines.some(l => isHoldingProduct(l.inventory_item_id) && !(l.holding_product_name || '').trim())) {
        setError('Describe each holding product — what the contract is for');
        return false;
      }
    } else if (!formData.inventory_item_id) {
      setError('Please select a product');
      return false;
    } else if (isHoldingProduct(formData.inventory_item_id) && !(formData.holding_product_name || '').trim()) {
      setError('Describe the holding product — what the contract is for');
      return false;
    }

    if (!formData.start_date) {
      setError('Please enter a start date');
      return false;
    }

    if (!formData.end_date) {
      setError('Please enter an end date');
      return false;
    }

    // Validate date range if both dates are provided
    if (formData.start_date && formData.end_date) {
      const start = new Date(formData.start_date);
      const end = new Date(formData.end_date);
      if (end < start) {
        setError('End date must be after start date');
        return false;
      }
    }

    // Validate numeric fields if provided
    const numericFields = [
      { field: 'quantity', label: 'Quantity' },
      { field: 'cost', label: 'Cost' },
      { field: 'price', label: 'Price' }
    ];

    for (const { field, label } of numericFields) {
      if (formData[field] !== '' && isNaN(parseFloat(formData[field]))) {
        setError(`${label} must be a valid number`);
        return false;
      }
    }

    return true;
  };

  const checkForDuplicate = async (overrides = {}) => {
    // A holding product is no product yet — there is nothing it could duplicate.
    if (isHoldingProduct(overrides.inventory_item_id ?? formData.inventory_item_id)) return null;
    try {
      const response = await fetch(`${API_URL}/api/pricing/contracts/check-duplicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          account_id: formData.account_id,
          inventory_item_id: overrides.inventory_item_id ?? formData.inventory_item_id,
          additive_id: overrides.additive_id !== undefined
            ? (overrides.additive_id === '' ? null : overrides.additive_id)
            : (formData.additive_id === '' ? null : formData.additive_id),
          start_date: formData.start_date,
          end_date: formData.end_date,
          exclude_id: overrides.exclude_id ?? (contract?.id || null),
          // A Blend / Compound contract spans several rows sharing a group id;
          // none of them should conflict with another line of the same group.
          exclude_group_id: contract?.contract_group_id || null,
          apply_scope: applyScope,
          specific_account_ids: applyScope === 'specific' ? scopeAccountIds : null
        })
      });

      if (response.ok) {
        const data = await response.json();
        return data.duplicate;
      }
      return null;
    } catch (error) {
      console.error('Error checking for duplicate:', error);
      return null;
    }
  };

  // For group saves (Blend / Compound), the product lives per-line — iterate
  // each line's product and return the first live overlap. This prevents
  // false positives against other contracts for the same customer whose
  // products aren't in the new group at all.
  const checkForDuplicateAcrossGroup = async () => {
    const existingIds = new Set(groupLines.map(l => l.id).filter(Boolean));
    for (const line of groupLines) {
      if (!line.inventory_item_id || isHoldingProduct(line.inventory_item_id)) continue;
      const dup = await checkForDuplicate({
        inventory_item_id: line.inventory_item_id,
        additive_id: line.additive_id || null
      });
      if (dup && !existingIds.has(dup.id)) return dup;
    }
    return null;
  };

  const saveContract = async ({ asDraft = false, startDateOverride = null } = {}) => {
    setLoading(true);

    try {
      // Blend (2) and Compound (3) groups: save via group endpoint.
      // Our contract number is a single string at the contract level.
      // The shared quantity is the OVERALL group total — every line in
      // the group gets that same value on the way to the DB so any
      // existing query that reads per-row quantity still works (but the
      // UI surfaces just one figure at the group level).
      if (multiProduct && groupLines.length > 0) {
        const sharedQuantity = formData.quantity === '' ? 0 : formData.quantity;
        const shared = {
          account_id: formData.account_id,
          start_date: startDateOverride || formData.start_date,
          end_date: formData.end_date,
          contract_number: formData.contract_number || null,
          quantity: sharedQuantity,
          rep_name: formData.rep_name || null,
          herd_type: formData.herd_type || null,
          address_line: formData.address_line || null,
          delivery_terms: formData.delivery_terms || null,
          notes: formData.notes || null,
          category_id: categoryId,
          is_draft: !!asDraft,
          // Pricing-group scope applies consistently across every line of the group.
          apply_scope: applyScope,
          specific_account_ids: applyScope === 'specific' ? scopeAccountIds : null
        };

        // Build a single-element additives array from the picked additive_id
        // so the backend writes to pricing.contract_additives. Empty array
        // when no additive is set.
        const additiveArrayFor = (id) => {
          if (!id) return [];
          const a = additives.find(x => x.id.toString() === id.toString());
          if (!a) return [];
          return [{
            additive_id: a.id,
            additive_code: a.code,
            additive_name: a.name,
            additive_price: a.price,
          }];
        };

        const lines = groupLines.map(line => ({
          id: line.id || null,
          ...productPayloadFor(line.inventory_item_id, line.holding_product_name),
          additive_id: line.additive_id === '' ? null : line.additive_id,
          additives: additiveArrayFor(line.additive_id),
          supplier_contact_id: line.supplier_contact_id === '' ? null : line.supplier_contact_id,
          mill: line.mill || null,
          // Every line carries the same group-level quantity (denormalised
          // so existing per-row consumers keep working without schema change).
          quantity: sharedQuantity,
          cost: line.cost === '' ? 0 : line.cost,
          price: line.price === '' ? 0 : line.price,
          margin: line.margin === '' ? 0 : line.margin,
          supplier_contract_number: line.supplier_contract_number || null,
          is_fulfilled_override: !!line.is_fulfilled_override
        }));

        const response = await fetch(`${API_URL}/api/pricing/contracts/group`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            group_id: contract?.contract_group_id || null,
            shared,
            lines,
            // The product group it was made from (2.27.0) — only once the
            // server can keep it; null forgets it.
            ...(groupInfo && groupInfo.ready ? { product_group_id: productGroupId || null, added_from_group: addedFromGroup } : {})
          })
        });

        if (response.ok) {
          // Pass the saved group id back so the parent table can refresh
          // only the affected rows instead of reloading the whole list.
          const data = await response.json().catch(() => null);
          const savedGroupId = data?.group_id || contract?.contract_group_id || null;
          onSave({ contractGroupId: savedGroupId });
        } else {
          const errorData = await response.json();
          setError(errorData.error || 'Unable to save contract group.');
        }
        setLoading(false);
        return;
      }

      const url = contract
        ? `${API_URL}/api/pricing/contracts/${contract.id}`
        : `${API_URL}/api/pricing/contracts`;

      const method = contract ? 'PUT' : 'POST';

      // Build single-element additives array for the backend junction. Empty
      // when no additive is selected.
      const additivesPayload = (() => {
        if (!formData.additive_id) return [];
        const a = additives.find(x => x.id.toString() === formData.additive_id.toString());
        if (!a) return [];
        return [{
          additive_id: a.id,
          additive_code: a.code,
          additive_name: a.name,
          additive_price: a.price,
        }];
      })();

      // Prepare data — empty numeric fields default to 0, not null
      const submitData = {
        ...formData,
        ...productPayloadFor(formData.inventory_item_id, formData.holding_product_name),
        start_date: startDateOverride || formData.start_date,
        additive_id: formData.additive_id === '' ? null : formData.additive_id,
        additives: additivesPayload,
        quantity: formData.quantity === '' ? 0 : formData.quantity,
        cost: formData.cost === '' ? 0 : formData.cost,
        price: formData.price === '' ? 0 : formData.price,
        margin: formData.margin === '' ? 0 : formData.margin,
        delivery_terms: formData.delivery_terms === '' ? null : formData.delivery_terms,
        contract_number: formData.contract_number || null,
        supplier_contract_number: formData.supplier_contract_number || null,
        is_draft: !!asDraft,
        apply_scope: applyScope,
        specific_account_ids: applyScope === 'specific' ? scopeAccountIds : null,
        // Single-product categories that aren't derivable from the item's
        // account code alone (Minerals 4, Spot 5, Bags 7, Other 8) pass their
        // category explicitly so the backend files the contract on the right tab.
        ...([4, 5, 7, 8].includes(categoryId) ? { category_id: categoryId } : {})
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        // Pass the saved contract id back so the parent table can refresh
        // only that row instead of reloading the whole list.
        const data = await response.json().catch(() => null);
        const savedContractId = data?.id || contract?.id || null;
        const savedGroupId = data?.contract_group_id || contract?.contract_group_id || null;
        onSave({ contractId: savedContractId, contractGroupId: savedGroupId });
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Unable to save contract pricing. Please check your entries and try again.');
      }
    } catch (error) {
      console.error('Error saving pricing:', error);
      setError('Connection error. Please check your network and try again.');
    } finally {
      setLoading(false);
    }
  };

  // `live`: the "Make live" button on a draft being edited — saves the form's
  // changes as a LIVE contract, through the same overlap check as any live
  // save, instead of keeping it a draft (Cam, 2026-10-22).
  const handleSubmit = async (e, { live = false } = {}) => {
    if (e?.preventDefault) e.preventDefault();

    // Validate form
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    // If the user explicitly opted to save as draft, skip the duplicate check
    // — drafts coexist with live contracts by design.
    if (formData.is_draft && !live) {
      setLoading(false);
      await saveContract({ asDraft: true });
      return;
    }

    // Check for duplicate contract (live-only). For group-based categories
    // (Blend, Compound) each line has its own product, so we must iterate.
    const duplicate = multiProduct
      ? await checkForDuplicateAcrossGroup()
      : await checkForDuplicate();
    setLoading(false);

    if (duplicate) {
      setDuplicateInfo(duplicate);
      setShowDuplicateConfirm(true);
      return;
    }

    // No duplicate, proceed with save
    await saveContract();
  };

  // Duplicate resolution option 1: shorten the existing contract so its
  // end_date is the day before the new contract starts, then save the new one
  // as live.
  const handleShortenExisting = async () => {
    if (!duplicateInfo) return;
    setLoading(true);
    const newEndDate = new Date(formData.start_date);
    newEndDate.setDate(newEndDate.getDate() - 1);
    const newEnd = newEndDate.toISOString().slice(0, 10);
    try {
      const res = await fetch(`${API_URL}/api/pricing/contracts/${duplicateInfo.id}/end-date`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ end_date: newEnd })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err.error || 'Failed to shorten existing contract.');
        setLoading(false);
        return;
      }
    } catch (e) {
      setError('Connection error shortening existing contract.');
      setLoading(false);
      return;
    }
    setShowDuplicateConfirm(false);
    setDuplicateInfo(null);
    await saveContract();
  };

  // Duplicate resolution option 2: nudge the new contract's start_date so it
  // begins the day after the existing one ends.
  const handleAdjustNewStart = async () => {
    if (!duplicateInfo) return;
    const existingEnd = new Date(duplicateInfo.end_date);
    existingEnd.setDate(existingEnd.getDate() + 1);
    const newStart = existingEnd.toISOString().slice(0, 10);
    setFormData(prev => ({ ...prev, start_date: newStart }));
    setShowDuplicateConfirm(false);
    setDuplicateInfo(null);
    await saveContract({ startDateOverride: newStart });
  };

  // Duplicate resolution option 3: save the new contract as a draft so it
  // doesn't affect pricing / allocation until the user resolves the overlap.
  const handleSaveAsDraft = async () => {
    setShowDuplicateConfirm(false);
    setDuplicateInfo(null);
    await saveContract({ asDraft: true });
  };

  const handleCancelDuplicate = () => {
    setShowDuplicateConfirm(false);
    setDuplicateInfo(null);
  };

  // The items on the contract now (every product row) — what a group would add to.
  const onContractItemIds = React.useMemo(
    () => groupLines.map(l => l.inventory_item_id).filter(v => v && !isHoldingProduct(v)).map(Number).filter(Number.isFinite),
    [groupLines]
  );

  // The price every priced product of the contract shares, if one — what a
  // product added from its group starts at.
  const sharedLinePrice = (() => {
    const prices = [...new Set(groupLines.map(l => l.price).filter(p => p !== '' && p !== null && p !== undefined && Number(p) > 0).map(p => String(Number(p))))];
    return prices.length === 1 ? prices[0] : '';
  })();

  // "Group: <name>" above the products of a contract made from a product
  // group, with the plain products the group has gained since (2.27.0).
  const renderRecordedGroup = () => {
    if (!productGroupId) return null;
    const live = (groupInfo?.groups || []).find(g => Number(g.id) === Number(productGroupId)) || null;
    const fresh = live ? newMembersOf(live, { categoryId, onContract: onContractItemIds }) : [];
    const view = mode === 'view';
    return (
      <div className="cpg-recorded">
        <ContractGroupBadge group={{ id: productGroupId, name: productGroupName || `Group #${productGroupId}`, is_archived: productGroupArchived }} />
        <span className="cpg-recorded-note">
          {productGroupArchived
            ? 'Made from this product group — the group has since been archived.'
            : `Made from this product group — its plain products${live && live.packs && live.packs.length === 1 ? ` ${live.packs[0].words}` : ''} are this contract's products.`}
        </span>
        {fresh.length > 0 && (
          <div className="buttons slideout">
            <div>
              <button type="button" className="btn-slideout" disabled={view}
                title={view ? 'Edit the contract to add them' : `Add ${fresh.map(m => m.item_code).join(', ')}`}
                onClick={() => setGroupRequest({ groupId: productGroupId, nonce: Date.now() })}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">playlist_add</span></div>
                <span className="btn-label">{addNewText(fresh.length)}</span>
              </button>
            </div>
          </div>
        )}
        <button type="button" className="btn-icon" disabled={view}
          title={view ? 'Edit the contract to change this' : 'Forget that this contract was made from the group — its products stay on it'}
          aria-label="Forget the product group"
          onClick={() => setProductGroupId(null)}>
          <span className="material-symbols-rounded">link_off</span>
        </button>
      </div>
    );
  };

  // "Applies to" control — only shown when the selected customer is part of a pricing
  // group (parent + pricing-linked children). Lets the price apply to the whole group,
  // just the owner account, or an explicit subset. Hidden for single-account customers.
  const renderApplyScope = () => {
    if (pricingGroup.length <= 1) return null;
    return (
      <div className="form-group" style={{ gridColumn: '1 / -1' }}>
        <ApplyScopeControl
          members={pricingGroup}
          accountId={formData.account_id}
          applyScope={applyScope}
          scopeAccountIds={scopeAccountIds}
          onScopeChange={setApplyScope}
          onAccountsChange={setScopeAccountIds}
          onSelectAccount={(id) => handleChange('account_id', String(id))}
          readOnly={mode === 'view'}
        />
      </div>
    );
  };

  return createPortal(
    <div className={`modal-overlay ${closing ? 'closing' : ''}`} onClick={handleClose}>
      <div className={`modal-content compound-pricing modal-container ${closing ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="contract-panel-left">
        <div className="modal-header">
          <h3 style={{ margin: "0", padding: "0" }}>{contract ? (mode === 'view' ? 'View Price' : 'Edit Price') : 'Add New Price'}</h3>
          <div className="header-actions" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
            {!multiProduct && (
              <div className="modal-fulfilled-toggle">
                <label
                  htmlFor="contract-fulfilled-override"
                  className="toggle-text"
                  style={{ cursor: mode === 'view' ? 'default' : 'pointer' }}
                  title="Force this contract to show as FULFILLED in tables regardless of qty/used maths."
                >
                  Mark this contract as fulfilled
                </label>
                <div className="checkbox-container">
                  <div className="check light">
                    <input
                      id="contract-fulfilled-override"
                      type="checkbox"
                      checked={!!formData.is_fulfilled_override}
                      onChange={(e) => handleChange('is_fulfilled_override', e.target.checked)}
                      disabled={mode === 'view'}
                    />
                    <label htmlFor="contract-fulfilled-override"></label>
                  </div>
                </div>
              </div>
            )}
            {formData.account_id && contractHistory.length > 0 && (
              <button
                type="button"
                className={`btn-sm ${showContractHistory ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setShowContractHistory(prev => !prev)}
                title="Contract History"
                style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: 'var(--font-xs)', padding: '4px 10px' }}
              >
                <span className="material-symbols-rounded" style={{ fontSize: '16px' }}>history</span>
                Contract History
                <span className="material-symbols-rounded" style={{ fontSize: '18px', transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)', transform: showContractHistory ? 'rotate(180deg)' : 'rotate(0deg)' }}>chevron_right</span>
              </button>
            )}
            {contract && (
              <button
                type="button"
                className="btn-icon"
                onClick={() => setShowHistory(true)}
                title="Audit History"
              >
                <span className="material-symbols-rounded">history</span>
              </button>
            )}
            <button className="btn-icon close-btn" onClick={handleClose} title="Close">
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
        </div>

        <div className="modal-body" style={{ position: 'relative' }}>
          {isInitializing && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(var(--bg-main-rgb), 0.85)', zIndex: 5 }}>
              <LoadingSpinner />
            </div>
          )}
          <form onSubmit={handleSubmit}>
            {error && (
              <div style={{
                padding: 'var(--spacing-md)',
                marginBottom: 'var(--spacing-md)',
                backgroundColor: 'rgba(var(--color-error-rgb), 0.1)',
                border: '1px solid var(--color-error)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--color-error)',
                fontSize: 'var(--font-sm)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-sm)'
              }}>
                <span className="material-symbols-rounded">error</span>
                <span>{error}</span>
              </div>
            )}
            <div className="form-grid">
              {/* New group (Blend 2 / Compound 3) top section — Row 1 / Row 2 /
                  Quick Select / Notes, then the existing Products block. */}
              {multiProduct && (
                <>
                  {/* Row 1 — Customer · Rep · Livestock Type · Our Contract No. */}
                  <div className="form-row-4" style={{ gridTemplateColumns: '2.5fr 1fr 1fr 1fr' }}>
                    <div className="form-group">
                      <label>Customer *</label>
                      <SearchableDropdown
                        value={formData.account_id}
                        onChange={(value) => handleChange('account_id', value)}
                        disabled={mode === 'view'}
                        options={customers.map(c => {
                          let addressLine = '';
                          if (c.address) {
                            try {
                              const addr = typeof c.address === 'string' ? JSON.parse(c.address) : c.address;
                              addressLine = addr.addressLine1 || '';
                            } catch (e) { /* ignore */ }
                          }
                          return {
                            value: c.id.toString(),
                            ...(c.account_no ? { badge: c.account_no } : {}),
                            label: c.company_name,
                            renderLabel: (
                              <>
                                {c.company_name}
                                {addressLine && <span className="address-line"> ({addressLine})</span>}
                              </>
                            ),
                            searchText: `${c.account_no || ''} ${c.company_name} ${addressLine}`
                          };
                        })}
                        placeholder="Select Customer…"
                        searchPlaceholder="Search by # or name..."
                        required
                      />
                    </div>
                    <div
                      className="form-group"
                      title="The rep is taken from the selected customer's contact record. Edit it there if it needs changing."
                    >
                      <label>Rep</label>
                      <SearchableDropdown
                        value={formData.rep_name}
                        onChange={(value) => handleChange('rep_name', value)}
                        disabled
                        options={[
                          { value: '', label: 'Select Rep…' },
                          ...reps.map(rep => ({
                            value: rep,
                            label: rep
                          }))
                        ]}
                        placeholder="Select Rep…"
                        searchPlaceholder="Search reps..."
                      />
                    </div>
                    <div className="form-group">
                      <label>Livestock Type</label>
                      <Dropdown
                        value={formData.herd_type}
                        onChange={(v) => handleChange('herd_type', v)}
                        disabled={mode === 'view'}
                        options={LIVESTOCK_TYPE_OPTIONS}
                        placeholder="Select…"
                      />
                    </div>
                    <OurContractNumberField
                      value={formData.contract_number}
                      onChange={(v) => handleChange('contract_number', v)}
                      disabled={mode === 'view'}
                    />
                  </div>

                  {renderApplyScope()}

                  {/* Row 2 — Quantity (Total) · Start Date · End Date · Delivery Terms */}
                  <div className="form-row-4" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                    <div className="form-group">
                      <label>Quantity (Total)</label>
                      <Input
                        type="text"
                        value={formData.quantity}
                        onChange={(e) => handleChange('quantity', e.target.value)}
                        disabled={mode === 'view'}
                        placeholder="Shared across all products"
                      />
                    </div>
                    <div className="form-group">
                      <DateInput
                        label="Start Date *"
                        value={formData.start_date}
                        onChange={(e) => handleChange('start_date', e.target.value)}
                        disabled={mode === 'view'}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <DateInput
                        label="End Date *"
                        value={formData.end_date}
                        onChange={(e) => handleChange('end_date', e.target.value)}
                        disabled={mode === 'view'}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Delivery Terms</label>
                      <Dropdown
                        value={formData.delivery_terms}
                        onChange={(value) => handleChange('delivery_terms', value)}
                        disabled={mode === 'view'}
                        options={[
                          { value: '', label: 'Select Delivery Terms…' },
                          { value: 'Direct Delivery', label: 'Direct Delivery' },
                          { value: 'Delivery', label: 'Delivery' },
                          { value: 'Customer Collection', label: 'Customer Collection' }
                        ]}
                        placeholder="Select Delivery Terms…"
                      />
                    </div>
                  </div>

                  {/* Quick Select — full width */}
                  <div className="form-group full-width">
                    <label>Quick Select</label>
                    <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                      {(() => {
                        const periods = [0, 1, 2].map(i => {
                          const p = getPeriodDatesOffset(i);
                          return { ...p, name: calculatePeriodName(p.startDate), isMatch: formData.start_date === p.startDate && formData.end_date === p.endDate };
                        });
                        return periods.map((p, i) => (
                            <button
                              key={i}
                              type="button"
                              className={p.isMatch ? 'btn-primary' : 'btn-secondary'}
                              style={{ flex: 1, justifyContent: 'center', fontSize: 'var(--font-sm)', padding: '10.5px', whiteSpace: 'nowrap' }}
                              disabled={mode === 'view'}
                              onClick={() => setFormData(prev => ({ ...prev, start_date: p.startDate, end_date: p.endDate }))}
                            >
                              {p.name}
                            </button>
                        ));
                      })()}
                    </div>
                  </div>

                  {/* Notes — full width */}
                  <div className="form-group full-width">
                    <label>Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => handleChange('notes', e.target.value)}
                      disabled={mode === 'view'}
                      placeholder="Additional notes..."
                      rows="3"
                    />
                  </div>
                </>
              )}

              {/* Top row — our (Excel Agri) contract number for Milk Powder (6).
                  Groups now render Our Contract No. in the new Row 1; spot (5)
                  carries its contract number down in the references row. */}
              {categoryId === 6 && (
              <div
                className="form-row-4"
                style={{ gridTemplateColumns: "minmax(220px, 1fr) 3fr" }}
              >
                <div className="form-group">
                  <label>Our Contract No.</label>
                  <Input
                    type="text"
                    value={formData.contract_number}
                    onChange={(e) => handleChange('contract_number', e.target.value)}
                    disabled={mode === 'view'}
                    placeholder="Optional…"
                  />
                </div>
                <div />
              </div>
              )}
              {!multiProduct && (
              <div className="form-row-4" style={{ gridTemplateColumns: categoryId === 5 ? "minmax(280px, 2.5fr) 150px 150px" : "1fr 155px 155px" }}>
                <div className="form-group">
                  <label>Customer *</label>
                  <SearchableDropdown
                    value={formData.account_id}
                    onChange={(value) => handleChange('account_id', value)}
                    disabled={mode === 'view'}
                    options={customers.map(c => {
                      let addressLine = '';
                      if (c.address) {
                        try {
                          const addr = typeof c.address === 'string' ? JSON.parse(c.address) : c.address;
                          addressLine = addr.addressLine1 || '';
                        } catch (e) { /* ignore */ }
                      }
                      return {
                        value: c.id.toString(),
                        ...(c.account_no ? { badge: c.account_no } : {}),
                        label: c.company_name,
                        renderLabel: (
                          <>
                            {c.company_name}
                            {addressLine && <span className="address-line"> ({addressLine})</span>}
                          </>
                        ),
                        searchText: `${c.account_no || ''} ${c.company_name} ${addressLine}`
                      };
                    })}
                    placeholder="Select Customer…"
                    searchPlaceholder="Search by # or name..."
                    required
                  />
                </div>

                <div className="form-group">
                  <DateInput
                    label="Start Date *"
                    value={formData.start_date}
                    onChange={(e) => handleChange('start_date', e.target.value)}
                    disabled={mode === 'view'}
                    required
                  />
                </div>
                <div className="form-group">
                  <DateInput
                    label="End Date *"
                    value={formData.end_date}
                    onChange={(e) => handleChange('end_date', e.target.value)}
                    disabled={mode === 'view'}
                    required
                  />
                </div>
                {categoryId !== 5 && (
                  <div className="form-group" style={{ gridColumn: 'span 3' }}>
                    <label>Quick Select</label>
                    <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                      {(() => {
                        const periods = [0, 1, 2].map(i => {
                          const p = getPeriodDatesOffset(i);
                          return { ...p, name: calculatePeriodName(p.startDate), isMatch: formData.start_date === p.startDate && formData.end_date === p.endDate };
                        });
                        return periods.map((p, i) => (
                            <button
                              key={i}
                              type="button"
                              className={p.isMatch ? 'btn-primary' : 'btn-secondary'}
                              style={{ flex: 1, justifyContent: 'center', fontSize: 'var(--font-sm)', padding: '10.5px', whiteSpace: 'nowrap' }}
                              disabled={mode === 'view'}
                              onClick={() => setFormData(prev => ({ ...prev, start_date: p.startDate, end_date: p.endDate }))}
                            >
                              {p.name}
                            </button>
                        ));
                      })()}
                    </div>
                  </div>
                )}

              </div>
              )}

              {!multiProduct && renderApplyScope()}

              {/* Several products (blends, compounds, minerals, bags, other): repeating product lines */}
              {multiProduct ? (
                <div style={{ gridColumn: '1 / -1', marginTop: 'var(--spacing-lg)', paddingTop: 'var(--spacing-xl)', borderTop: '1px solid var(--border-secondary)' }}>
                  <h4 style={{ margin: '0 0 var(--spacing-sm)', color: 'var(--text-secondary)' }}>Products</h4>
                  {renderRecordedGroup()}
                  {groupLines.map((line, idx) => (
                    <div key={idx} className="product-group-line" style={{ padding: 'var(--spacing-md)', borderRadius: 'var(--radius-lg)', background: 'rgba(var(--bg-main-rgb), 0.5)', marginBottom: 'var(--spacing-sm)', position: 'relative' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '2fr 0.75fr 2fr', gap: 'var(--spacing-md)' }}>
                        <div className="form-group product-select">
                          <label>Product *</label>
                          <SearchableDropdown
                            value={line.inventory_item_id}
                            onChange={(value) => handleGroupLineChange(idx, 'inventory_item_id', value)}
                            disabled={mode === 'view'}
                            options={[holdingProductOption(), ...products.map(p => ({
                              value: p.id.toString(),
                              ...(p.item_code ? { badge: p.item_code } : {}),
                              label: p.item_name,
                              searchText: `${p.item_code || ''} ${p.item_name}`,
                              renderLabel: (
                                <span className={`option-name${p.is_archived ? ' is-archived' : ''}`}>
                                  <span className="option-name-text">{p.item_name}</span>
                                  {/* Inside the name, so it follows the last word rather than
                                      wrapping the name and dropping underneath it. */}
                                  {p.is_archived && <span className="option-status-badge is-archived">ARCHIVED</span>}
                                </span>
                              )
                            }))]}
                            placeholder="Select Product…"
                            searchPlaceholder="Search products..."
                            required
                          />
                          {isHoldingProduct(line.inventory_item_id) && (
                            <HoldingProductDescription
                              id={`holding-product-${idx}`}
                              value={line.holding_product_name}
                              onChange={(v) => handleGroupLineChange(idx, 'holding_product_name', v)}
                              disabled={mode === 'view'}
                            />
                          )}
                        </div>
                        <div className="form-group additive-select">
                          <label>Additive</label>
                          <SearchableDropdown
                            value={line.additive_id}
                            onChange={(value) => handleGroupLineChange(idx, 'additive_id', value)}
                            disabled={mode === 'view'}
                            options={[{ value: '', label: 'None' }, ...additives.map(a => ({ value: a.id.toString(), badge: a.code || ' ', label: a.name, searchText: `${a.code || ''} ${a.name}` }))]}
                            placeholder="Select..."
                            searchPlaceholder="Search..."
                          />
                        </div>
                        <div className="form-group supplier-select">
                          <label>Supplier</label>
                          <SearchableDropdown
                            value={line.supplier_contact_id}
                            onChange={(value) => handleGroupLineChange(idx, 'supplier_contact_id', value)}
                            disabled={mode === 'view'}
                            options={suppliers.map(c => {
                              let al = '';
                              if (c.address) { try { const addr = typeof c.address === 'string' ? JSON.parse(c.address) : c.address; al = addr.addressLine1 || ''; } catch (e) { /* */ } }
                              return { value: c.id.toString(), ...(c.account_no ? { badge: c.account_no } : {}), label: c.company_name || ' ', renderLabel: (<>{c.company_name}{al && <span className="address-line"> ({al})</span>}</>), searchText: `${c.account_no || ''} ${c.company_name} ${al}` };
                            })}
                            placeholder="Select Supplier…"
                            searchPlaceholder="Search by # or name..."
                          />
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 'var(--spacing-md)', marginTop: 'var(--spacing-sm)' }}>
                        <div className="form-group">
                          <label>Supplier Contract</label>
                          <div style={{ display: 'flex', gap: 'var(--spacing-xs)', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                              <Input
                                type="text"
                                value={line.supplier_contract_number || ''}
                                onChange={(e) => handleGroupLineChange(idx, 'supplier_contract_number', e.target.value)}
                                disabled={mode === 'view'}
                                placeholder="Supplier's contract #"
                              />
                            </div>
                            <button
                              type="button"
                              className="btn-icon"
                              title="Apply this contract number to all suppliers (overwrites all, including blank)"
                              onClick={() => applyContractNumberToAll(line.supplier_contract_number || '')}
                              disabled={mode === 'view'}
                            >
                              <span className="material-symbols-rounded">content_copy</span>
                            </button>
                          </div>
                        </div>
                        <div className="form-group">
                          {(() => {
                            const inv = costNoteFor(line.inventory_item_id, line.additive_id, line.cost);
                            return (
                              <label>
                                Cost (£)
                                {inv !== null && (
                                  <span className="cost-differs-note"
                                        title="The cost on the inventory item, including any additive. Editing this line does not change the item.">
                                    inventory says {formatPrice(inv)}
                                  </span>
                                )}
                              </label>
                            );
                          })()}
                          <Input type="number" step="0.01" value={line.cost} onChange={(e) => handleGroupLineChange(idx, 'cost', e.target.value)} disabled={mode === 'view'} placeholder="0" />
                        </div>
                        <div className="form-group">
                          {(() => {
                            // Suggested price per line — additive is already
                            // baked into line.cost via handleGroupLineChange,
                            // so the helper just adds the margin on top.
                            if (mode === 'view' || line.price) {
                              return <label>Price (£)</label>;
                            }
                            const sug = computeSuggestedPrice(categoryId, { cost: line.cost });
                            if (!sug) return <label>Price (£)</label>;
                            return (
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <label>Price (£)</label>
                                <div
                                  onClick={() => handleGroupLineChange(idx, 'price', sug.toFixed(2))}
                                  className='suggested-price'
                                  style={{ cursor: 'pointer' }}
                                >
                                  <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}> Suggested is £{sug.toFixed(2)}</span>
                                </div>
                              </div>
                            );
                          })()}
                          <Input type="number" step="0.01" value={line.price} onChange={(e) => handleGroupLineChange(idx, 'price', e.target.value)} disabled={mode === 'view'} placeholder="0" />
                        </div>
                        <div className="form-group">
                          <label>Margin (£)</label>
                          <Input type="number" step="0.01" value={line.margin} placeholder="Auto" disabled />
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginTop: 'var(--spacing-sm)' }}>
                        <div className="checkbox-container">
                          <div className="check light">
                            <input
                              id={`group-fulfilled-override-${idx}`}
                              type="checkbox"
                              checked={!!line.is_fulfilled_override}
                              onChange={(e) => handleGroupLineChange(idx, 'is_fulfilled_override', e.target.checked)}
                              disabled={mode === 'view'}
                            />
                            <label htmlFor={`group-fulfilled-override-${idx}`}></label>
                          </div>
                        </div>
                        <label
                          htmlFor={`group-fulfilled-override-${idx}`}
                          style={{ cursor: mode === 'view' ? 'default' : 'pointer', fontSize: 'var(--font-sm)' }}
                          title="Force this line to show as FULFILLED in tables regardless of qty/used maths."
                        >
                          Mark this product as fulfilled
                        </label>
                      </div>
                      {groupLines.length > 1 && mode !== 'view' && (
                        <button type="button" className="btn-icon btn-danger" onClick={() => removeGroupLine(idx)}
                          style={{ position: 'absolute', top: '8px', right: '8px' }} title="Remove line">
                          <span className="material-symbols-rounded" style={{ fontSize: '18px' }}>close</span>
                        </button>
                      )}
                    </div>
                  ))}
                  {mode !== 'view' && (
                    <button type="button" className="btn-full" onClick={addGroupLine}
                      style={{ margin: '0 auto', display: 'flex'}} title="Add product line">
                      <span className="icon-wrapper">
                        <span className="material-symbols-rounded">add</span>
                      </span>
                      <span className="btn-label">Add Product Line</span>
                    </button>
                  )}
                  {mode !== 'view' && (
                    <ContractProductGroupPicker
                      info={groupInfo}
                      categoryId={categoryId}
                      onContract={onContractItemIds}
                      request={groupRequest}
                      defaultPrice={sharedLinePrice}
                      onAdd={addFromGroup}
                    />
                  )}
                </div>
              ) : (
              <>
              <div style={{ gridColumn: "1 / -1", display: "grid", gridTemplateColumns: categoryId === 6 ? "2fr 2fr" : "2fr 0.75fr 2fr", gap: "var(--spacing-md)" }}>
                <div className="form-group product-select">
                  <label>Product *</label>
                  <SearchableDropdown
                    value={formData.inventory_item_id}
                    onChange={(value) => handleChange('inventory_item_id', value)}
                    disabled={mode === 'view'}
                    options={[holdingProductOption(), ...products.map(p => ({
                      value: p.id.toString(),
                      ...(p.item_code ? { badge: p.item_code } : {}),
                      label: p.item_name,
                      searchText: `${p.item_code || ''} ${p.item_name}`,
                      renderLabel: (
                        <span className={`option-name${p.is_archived ? ' is-archived' : ''}`}>
                          <span className="option-name-text">{p.item_name}</span>
                          {/* Inside the name, so it follows the last word rather than
                              wrapping the name and dropping underneath it. */}
                          {p.is_archived && <span className="option-status-badge is-archived">ARCHIVED</span>}
                        </span>
                      )
                    }))]}
                    placeholder="Select Product…"
                    searchPlaceholder="Search products..."
                    required
                  />
                  {isHoldingProduct(formData.inventory_item_id) && (
                    <HoldingProductDescription
                      id="holding-product"
                      value={formData.holding_product_name}
                      onChange={(v) => handleChange('holding_product_name', v)}
                      disabled={mode === 'view'}
                    />
                  )}
                </div>

                {/* Milk Powder (6) is sold as a single product per contract —
                    no additives. Hidden for that category. */}
                {categoryId !== 6 && (
                  <div className="form-group additive-select">
                    <label>Additive</label>
                    <SearchableDropdown
                      value={formData.additive_id}
                      onChange={(value) => handleChange('additive_id', value)}
                      disabled={mode === 'view'}
                      options={[
                        { value: '', label: 'None' },
                        ...additives.map(a => ({
                          value: a.id.toString(),
                          badge: a.code || ' ',
                          label: a.name,
                          searchText: `${a.code || ''} ${a.name}`
                        }))
                      ]}
                      placeholder="Select..."
                      searchPlaceholder="Search..."
                    />
                  </div>
                )}

                <div className="form-group supplier-select">
                  <label>Supplier</label>
                  <SearchableDropdown
                    value={formData.supplier_contact_id}
                    onChange={(value) => handleChange('supplier_contact_id', value)}
                    disabled={mode === 'view'}
                    options={suppliers.map(c => {
                      let addressLine = '';
                      if (c.address) {
                        try {
                          const addr = typeof c.address === 'string' ? JSON.parse(c.address) : c.address;
                          addressLine = addr.addressLine1 || '';
                        } catch (e) { /* ignore */ }
                      }
                      return {
                        value: c.id.toString(),
                        ...(c.account_no ? { badge: c.account_no } : {}),
                        label: c.company_name || ' ',
                        renderLabel: (
                          <>
                            {c.company_name}
                            {addressLine && <span className="address-line"> ({addressLine})</span>}
                          </>
                        ),
                        searchText: `${c.account_no || ''} ${c.company_name} ${addressLine}`
                      };
                    })}
                    placeholder="Select Supplier…"
                    searchPlaceholder="Search by # or name..."
                  />
                </div>
              </div>

              <div className="form-row-4" style={categoryId === 5 ? { gridTemplateColumns: 'repeat(4, 1fr)' } : {}}>
                {categoryId !== 5 && (
                  <div className="form-group">
                    <label>Quantity</label>
                    <Input
                      type="text"
                      value={formData.quantity}
                      onChange={(e) => handleChange('quantity', e.target.value)}
                      disabled={mode === 'view'}
                      placeholder="0"
                    />
                  </div>
                )}

                <div className="form-group">
                  {(() => {
                    const inv = costNoteFor(formData.inventory_item_id, formData.additive_id, formData.cost);
                    return (
                      <label>
                        Cost (£)
                        {inv !== null && (
                          <span className="cost-differs-note"
                                title="The cost on the inventory item, including any additive. Editing this does not change the item.">
                            inventory says {formatPrice(inv)}
                          </span>
                        )}
                      </label>
                    );
                  })()}
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.cost}
                    onChange={(e) => handleChange('cost', e.target.value)}
                    disabled={mode === 'view'}
                    placeholder="0"
                  />
                </div>

                <div className="form-group">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label>Price (£)</label>
                    {suggestedPrice && (
                      <div
                        onClick={() => handleChange('price', suggestedPrice.toFixed(2))}
                        className='suggested-price'
                        style={{ cursor: 'pointer' }}
                      >
                        <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}> Suggested is £{suggestedPrice.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => handleChange('price', e.target.value)}
                    disabled={mode === 'view'}
                    placeholder="0"
                  />
                </div>

                <div className="form-group">
                  <label>Margin (£)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.margin}
                    onChange={(e) => handleChange('margin', e.target.value)}
                    placeholder="Auto-calculated"
                    disabled
                  />
                </div>

                {categoryId === 5 && (
                  <div
                    className="form-group"
                    title="The rep is taken from the selected customer's contact record. Edit it there if it needs changing."
                  >
                    <label>Rep Name</label>
                    <SearchableDropdown
                      value={formData.rep_name}
                      onChange={(value) => handleChange('rep_name', value)}
                      disabled
                      options={[
                        { value: '', label: 'Select Rep…' },
                        ...reps.map(rep => ({ value: rep, label: rep }))
                      ]}
                      placeholder="Select Rep…"
                      searchPlaceholder="Search reps..."
                    />
                  </div>
                )}
              </div>
              </>
              )}

              {/* References row. Spot (5): Our Contract # · Supplier Contract ·
                  Herd · Delivery (Rep sits with the pricing row above). Other
                  non-group flows keep Rep · Herd · Delivery here. Groups (2/3)
                  render these fields in the new top section instead. */}
              {!multiProduct && (
              <div className="form-row-4" style={categoryId === 5 ? { gridTemplateColumns: 'repeat(4, 1fr)' } : {}}>
                {categoryId === 5 && (
                  <div className="form-group">
                    <label>Our Contract No.</label>
                    <Input
                      type="text"
                      value={formData.contract_number}
                      onChange={(e) => handleChange('contract_number', e.target.value)}
                      disabled={mode === 'view'}
                      placeholder="Optional…"
                    />
                  </div>
                )}
                {categoryId === 5 && (
                  <div className="form-group">
                    <label>Supplier Contract</label>
                    <Input
                      type="text"
                      value={formData.supplier_contract_number}
                      onChange={(e) => handleChange('supplier_contract_number', e.target.value)}
                      disabled={mode === 'view'}
                      placeholder="Supplier's contract #"
                    />
                  </div>
                )}

                {categoryId !== 5 && (
                  <div
                    className="form-group"
                    title="The rep is taken from the selected customer's contact record. Edit it there if it needs changing."
                  >
                    <label>Rep Name</label>
                    <SearchableDropdown
                      value={formData.rep_name}
                      onChange={(value) => handleChange('rep_name', value)}
                      disabled
                      options={[
                        { value: '', label: 'Select Rep…' },
                        ...reps.map(rep => ({
                          value: rep,
                          label: rep
                        }))
                      ]}
                      placeholder="Select Rep…"
                      searchPlaceholder="Search reps..."
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Livestock Type</label>
                  <Dropdown
                    value={formData.herd_type}
                    onChange={(value) => handleChange('herd_type', value)}
                    disabled={mode === 'view'}
                    options={LIVESTOCK_TYPE_OPTIONS}
                    placeholder="Select…"
                  />
                </div>

                <div className="form-group">
                  <label>Delivery Terms</label>
                  <Dropdown
                    value={formData.delivery_terms}
                    onChange={(value) => handleChange('delivery_terms', value)}
                    disabled={mode === 'view'}
                    options={[
                      { value: '', label: 'Select Delivery Terms…' },
                      { value: 'Direct Delivery', label: 'Direct Delivery' },
                      { value: 'Delivery', label: 'Delivery' },
                      { value: 'Customer Collection', label: 'Customer Collection' }
                    ]}
                    placeholder="Select Delivery Terms…"
                  />
                </div>
              </div>
              )}

              {!multiProduct && (
              <div className="form-group full-width">
                <label>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  disabled={mode === 'view'}
                  placeholder="Additional notes..."
                  rows="3"
                />
              </div>
              )}

            </div>

            {/* Linked POs panel — only relevant for existing contracts (an id
                exists). Hidden in add mode since there's nothing to link to
                yet. Interactive (unlink + add manual link) when mode is 'edit'.
                For Blend/Compound group contracts (multiple members under one
                contract_group_id), pass every member's id so the panel can
                aggregate linked POs across the whole group. Manual linking is
                disabled in multi-entity mode (the panel handles that itself). */}
            {contract && contract.id && (() => {
              const memberIds = multiProduct && contract.contract_group_id
                ? groupLines.map(l => l.id).filter(Boolean)
                : null;
              if (memberIds && memberIds.length > 1) {
                return <LinkedPurchaseOrdersPanel kind="contract" entityIds={memberIds} mode={mode} />;
              }
              return <LinkedPurchaseOrdersPanel kind="contract" entityId={contract.id} mode={mode} />;
            })()}

            {/* The order lines counted against it — automatic or added by
                hand (28 Oct 2026); adding and taking off in edit mode. */}
            {contract && contract.id && (
              <ContractOrdersPanel
                set={{ kind: 'contract', ids: (multiProduct && contract.contract_group_id
                  ? groupLines.map(l => l.id).filter(Boolean) : [contract.id]) }}
                canEdit={mode !== 'view'}
                title="Orders counted against this contract"
              />
            )}
          </form>
        </div>
        <div className="modal-footer">
            {mode !== 'view' && (
              <div>
                <button type="button" className="btn-slideout btn-slideout-primary" disabled={loading} onClick={handleSubmit}>
                  <div className="icon-wrapper">
                    <span className={`material-symbols-rounded ${loading ? 'spinning' : ''}`}>
                      {loading ? 'sync' : 'save'}
                    </span>
                  </div>
                  <span className="btn-label">{loading ? 'Saving...' : (contract ? (formData.is_draft ? 'Update draft' : 'Update') : 'Save')}</span>
                </button>
              </div>
            )}
            {mode !== 'view' && contract?.is_draft && (
              <div>
                <button
                  type="button"
                  className="btn-slideout"
                  disabled={loading}
                  onClick={(e) => handleSubmit(e, { live: true })}
                  title="Save these changes and make the contract live. It is checked against live contracts for the same product and customer first — two contracts in one period are fine as long as their dates don't overlap."
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">publish</span>
                  </div>
                  <span className="btn-label">Make live</span>
                </button>
              </div>
            )}
            {mode !== 'view' && (
              <div>
                <button
                  type="button"
                  className={`btn-slideout draft-toggle-btn ${formData.is_draft ? 'active' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, is_draft: !prev.is_draft }))}
                  disabled={loading}
                  title="Drafts do not affect pricing lookups, allocation or remaining quantity until promoted to live."
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">{formData.is_draft ? 'drafts' : 'publish'}</span>
                  </div>
                  <span className="btn-label">{formData.is_draft ? 'Saving as Draft' : 'Save as Draft'}</span>
                </button>
              </div>
            )}
            {contract && multiProduct && mode !== 'view' && (
              <div>
                <button type="button" className={`btn-slideout ${contract.is_on_hold ? 'btn-unhold' : 'btn-hold'}`} disabled={loading}
                  onClick={async () => {
                    try {
                      await fetch(`${API_URL}/api/pricing/contracts/${contract.id}/hold`, { method: 'PATCH', credentials: 'include' });
                      onSave();
                    } catch (err) { console.error('Error toggling hold:', err); }
                  }}>
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">{contract.is_on_hold ? 'play_circle' : 'pause_circle'}</span>
                  </div>
                  <span className="btn-label">{contract.is_on_hold ? 'Resume' : 'Hold'}</span>
                </button>
              </div>
            )}
            <div style={{ width: '100%', textAlign: 'right' }}>
              {mode === 'view' ? (
                <button type="button" className="btn-slideout btn-slideout-primary" onClick={() => setMode('edit')}>
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">edit</span>
                  </div>
                  <span className="btn-label">Edit</span>
                </button>
              ) : (
                <button data-read-safe type="button" className="btn-round btn-danger" onClick={handleClose}>
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">close</span>
                  </div>
                  <span className="btn-label">Cancel</span>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className={`contract-panel-right ${showContractHistory ? 'open' : ''}`}>
          <div className="right-panel-inner">
          <div className="right-panel-header">
            <h3>Contract History</h3>
            <button className="btn-icon" onClick={() => setShowContractHistory(false)} title="Close panel">
              <span className="material-symbols-rounded">chevron_left</span>
            </button>
          </div>
          <div className="right-panel-content">
            {contractHistory.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', padding: 'var(--spacing-md)', textAlign: 'center' }}>No contract history</p>
            ) : (
              <div className="history-table-wrapper">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Dates</th>
                    <th>Contract</th>
                    <th style={{ textAlign: 'right' }}>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {contractHistory.map(c => (
                    <tr key={c.id} className={`${c.id === contract?.id ? 'current-contract' : ''} ${c.is_on_hold ? 'on-hold' : ''}`}>
                      <td>
                        <div>{c.product_name}</div>
                        {c.additive_name && <div style={{ color: 'var(--text-muted)' }}>+ {c.additive_name}</div>}
                        {c.is_on_hold && <span className="badge" style={{ background: 'var(--color-warning)', color: '#000', fontSize: '0.65em' }}>HOLD</span>}
                      </td>
                      <td>
                        <div style={{ fontSize: '0.9em', whiteSpace: 'nowrap', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                          <span>{c.start_date ? new Date(c.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '-'}</span>
                          <span>{c.end_date ? new Date(c.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '-'}</span>
                        </div>
                      </td>
                      <td>{c.contract_number || '-'}</td>
                      <td style={{ textAlign: 'right' }}>
                        <div>{c.price ? formatPrice(c.price, { showSymbol: false }) : '-'}</div>
                        {c.cost && <div style={{ color: 'var(--text-muted)' }}>({formatPrice(c.cost, { showSymbol: false })})</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
          </div>
        </div>
      </div>

      {showDuplicateConfirm && duplicateInfo && createPortal(
        <div className="modal-overlay" onClick={handleCancelDuplicate}>
          <div className="modal-content modal-md duplicate-resolve-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
                <span className="material-symbols-rounded" style={{ color: 'var(--color-warning)' }}>warning</span>
                Overlapping contract
              </h3>
              <button className="btn-icon close-btn" onClick={handleCancelDuplicate} title="Close">
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="modal-body" style={{ padding: 'var(--spacing-lg) var(--spacing-xl)' }}>
              <p style={{ marginTop: 0 }}>An existing contract overlaps the dates on the new one:</p>
              <div className="duplicate-info" style={{ background: 'rgba(var(--bg-main-rgb), 0.4)', padding: 'var(--spacing-md)', borderRadius: 'var(--radius-md)', margin: 'var(--spacing-sm) 0 var(--spacing-lg)' }}>
                <div className="field"><strong>Customer:</strong> {duplicateInfo.customer}</div>
                <div className="field"><strong>Product:</strong> {duplicateInfo.product_code} — {duplicateInfo.product}</div>
                {duplicateInfo.additive && (
                  <div className="field"><strong>Additive:</strong> {duplicateInfo.additive_code} — {duplicateInfo.additive}</div>
                )}
                <div className="field">
                  <strong>Existing period:</strong>{' '}
                  {new Date(duplicateInfo.start_date).toLocaleDateString('en-GB')}
                  {' — '}
                  {new Date(duplicateInfo.end_date).toLocaleDateString('en-GB')}
                </div>
                <div className="field">
                  <strong>New contract starts:</strong>{' '}
                  {new Date(formData.start_date).toLocaleDateString('en-GB')}
                </div>
              </div>
              <p style={{ margin: '0 0 var(--spacing-md)', color: 'var(--text-secondary)' }}>How would you like to resolve it?</p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                  <button type="button" className="btn-round" onClick={handleShortenExisting} disabled={loading} title="Shorten existing contract">
                    <span className="icon-wrapper"><span className="material-symbols-rounded">content_cut</span></span>
                  </button>
                  <span style={{ flex: 1 }}>Shorten existing contract to end the day before this one starts</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                  <button type="button" className="btn-round" onClick={handleAdjustNewStart} disabled={loading} title="Move new contract start">
                    <span className="icon-wrapper"><span className="material-symbols-rounded">skip_next</span></span>
                  </button>
                  <span style={{ flex: 1 }}>Move this contract's start to the day after the existing one ends</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                  <button type="button" className="btn-round" onClick={handleSaveAsDraft} disabled={loading} title="Save as draft">
                    <span className="icon-wrapper"><span className="material-symbols-rounded">drafts</span></span>
                  </button>
                  <span style={{ flex: 1 }}>Save as draft — resolve later, won't affect pricing or allocation</span>
                </div>
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', padding: 'var(--spacing-lg) var(--spacing-xl)' }}>
              <button data-read-safe type="button" className="btn-round btn-danger" onClick={handleCancelDuplicate} disabled={loading}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                <span className="btn-label">Cancel</span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showHistory && contract && (
        <AuditHistoryModal
          isOpen={showHistory}
          onClose={() => setShowHistory(false)}
          entityType="contract_pricing"
          entityId={contract.id}
          entityIdentifier={`${contract.customer || ''} - ${contract.product || ''}`}
        />
      )}
    </div>,
    document.body
  );
}

export default ContractPricingModal;